// ============================================================================
// BUG-2026-0109 — Computed Report Values card (extracted from
// TriaxialAnalysis.tsx) + operator-selected φ' inputs.
//
// The engineer can type a "Selected φ'_max / φ'_end" directly under the
// computed φ' values. The selection persists via its own PATCH endpoint
// (independent of the Analysis Picker, so a picker save can never clobber
// it) and the generated report prints the selected value when present,
// falling back to the computed cache.
// ============================================================================

import { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Paper, TextField, Typography,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import type { TriaxialGeneral } from '../../../types/triaxial';
import type { SlotSnapshot } from './triaxialPickerResolver';
import type { TriaxialSlot } from './triaxialPickerColors';

interface Props {
  general: TriaxialGeneral;
  snapshots: Record<TriaxialSlot, SlotSnapshot>;
  readOnly?: boolean;
  onSaveSelectedPhi: (phiMaxDeg: number | null, phiEndDeg: number | null) => Promise<void>;
}

const fmt = (v: number | null | undefined, d = 2): string => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(d) : '—';
};

const numToStr = (v: number | null | undefined): string =>
  (v === null || v === undefined ? '' : String(v));

const strToNum = (s: string): number | null => {
  if (s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

export const TriaxialComputedValuesCard = ({
  general, snapshots, readOnly, onSaveSelectedPhi,
}: Props) => {
  // Selected φ' inputs — seeded from the persisted row, re-seeded on reload.
  const [phiMaxStr, setPhiMaxStr] = useState<string>(() => numToStr(general.selectedPhiMaxDeg ?? null));
  const [phiEndStr, setPhiEndStr] = useState<string>(() => numToStr(general.selectedPhiEndDeg ?? null));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPhiMaxStr(numToStr(general.selectedPhiMaxDeg ?? null));
    setPhiEndStr(numToStr(general.selectedPhiEndDeg ?? null));
  }, [general.selectedPhiMaxDeg, general.selectedPhiEndDeg]);

  const dirty =
    strToNum(phiMaxStr) !== (general.selectedPhiMaxDeg ?? null)
    || strToNum(phiEndStr) !== (general.selectedPhiEndDeg ?? null);

  const onSave = async () => {
    setSaving(true);
    try {
      await onSaveSelectedPhi(strToNum(phiMaxStr), strToNum(phiEndStr));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="overline" sx={{ fontWeight: 700, color: 'primary.main' }}>
        Computed Report Values — read from the persisted snapshot
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mt: 1 }}>
        <Statline label="Skempton's A at max" value={fmt(general.computedSkemptonA, 2)} />
        <Statline label="Consolidation Stress p'" value={`${fmt(general.computedConsolidationStressKPa, 0)} kPa`} />
        <Statline label="τ_max" value={`${fmt(general.computedTauMaxKPa, 0)} kPa`} />
        <Statline label="τ_end" value={`${fmt(general.computedTauEndKPa, 0)} kPa`} />
        <Statline label="φ'_max" value={`${fmt(general.computedPhiMaxDeg, 1)}°`} />
        <Statline label="φ'_end" value={`${fmt(general.computedPhiEndDeg, 1)}°`} />
      </Box>

      {/* ── Selected φ' (report overrides) — directly under the computed φ' ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mt: 1.5, alignItems: 'center' }}>
        <TextField
          size="small" type="number" label="Selected φ'_max (°)"
          inputProps={{ step: 0.1 }}
          value={phiMaxStr}
          onChange={(e) => setPhiMaxStr(e.target.value)}
          disabled={readOnly || saving}
        />
        <TextField
          size="small" type="number" label="Selected φ'_end (°)"
          inputProps={{ step: 0.1 }}
          value={phiEndStr}
          onChange={(e) => setPhiEndStr(e.target.value)}
          disabled={readOnly || saving}
        />
        <Button
          variant="contained" size="small"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save sx={{ fontSize: 14 }} />}
          onClick={onSave}
          disabled={readOnly || saving || !dirty}
          sx={{ textTransform: 'none', justifySelf: 'start' }}
        >
          {saving ? 'Saving…' : 'Save Selected Values'}
        </Button>
        <Typography variant="caption" color="text.secondary">
          When set, the report prints these selected φ' values instead of the computed ones.
          Clear a field and save to fall back to the computed value.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mt: 1.5 }}>
        <Statline label="Max q (snapshot)" value={`${fmt(snapshots.max.deviatorStressKPa, 0)} kPa`} />
        <Statline label="Peak σ′₁/σ′₃ (snapshot)" value={fmt(snapshots.peak.effStressRatio, 2)} />
        <Statline label="End εₐ (snapshot)" value={`${fmt(snapshots.end.axialStrainPct, 2)} %`} />
        <Statline label="Residual q (snapshot)" value={`${fmt(snapshots.residual.deviatorStressKPa, 0)} kPa`} />
      </Box>
    </Paper>
  );
};

function Statline({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
    </Box>
  );
}
